import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Upload, Trash2, ExternalLink, ShieldCheck, Info, Sparkles, Loader2, Download, Share2, Ban, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/date";
import { toast } from "sonner";
import { PageHeader, StatusPill } from "@/components/ui-helpers";
import EmptyState from "@/components/EmptyState";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["Passport", "Visa", "Ticket", "Boarding Pass", "Insurance", "Invoice", "Business Licence", "Licence", "ID", "Other"];
const KW = [["passport", "Passport"], ["visa", "Visa"], ["boarding", "Boarding Pass"], ["ticket", "Ticket"], ["insurance", "Insurance"], ["invoice", "Invoice"], ["receipt", "Invoice"]];
function inferCategory(name) { const n = (name || "").toLowerCase(); for (const [k, v] of KW) { if (n.includes(k)) return v; } return null; }

export default function DocumentsPage() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Passport", number: "", issue_date: "", expiry_date: "", family_member: "" });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiFilled, setAiFilled] = useState(null);
  const [bulkMeta, setBulkMeta] = useState([]);
  const [famFilter, setFamFilter] = useState("");
  const { user, refreshUser } = useAuth();
  const isAgent = user?.role === "travel_agent";
  const [shareEmail, setShareEmail] = useState(user?.share_email || "");
  const [mailSig, setMailSig] = useState(user?.mail_signature || "");
  const [mailSubj, setMailSubj] = useState(user?.mail_subject || "");

  const saveMailPrefs = async () => {
    try { await api.put("/me/mail-prefs", { signature: mailSig, subject: mailSubj }); await refreshUser(); toast.success("Email defaults saved"); }
    catch (e) { toast.error("Could not save email defaults"); }
  };

  const saveShareEmail = async () => {
    try { await api.put("/me/share-email", { email: shareEmail }); toast.success("Sharing email saved"); }
    catch (e) { toast.error("Could not save email"); }
  };

  const load = () => api.get("/records?type=document").then((r) => setItems(r.data.records)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const analyzedRef = useRef("");
  useEffect(() => {
    if (files.length > 1) {
      setBulkMeta(files.map((f) => ({ title: f.name.replace(/\.[^.]+$/, ""), category: inferCategory(f.name) || "Other", number: "", issue_date: "", expiry_date: "" })));
    } else {
      setBulkMeta([]);
      if (files.length === 1) {
        const sig = `${files[0].name}:${files[0].size}`;
        if (analyzedRef.current !== sig) { analyzedRef.current = sig; analyze(); }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const analyzeBulk = async () => {
    setAnalyzing(true);
    const next = [...bulkMeta];
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData(); fd.append("file", files[i]);
      try {
        const { data } = await api.post("/documents/analyze", fd);
        const d = data.detected || data.suggestion || {};
        next[i] = { title: d.title || next[i].title, category: d.category || next[i].category, number: d.number || "", issue_date: d.issue_date || "", expiry_date: d.expiry_date || "" };
        setBulkMeta([...next]);
      } catch (e) { console.warn("Bulk AI classification failed for file", files[i]?.name, e); }
    }
    setAnalyzing(false);
    toast.success("AI classified your files — review and confirm");
  };

  const analyze = async () => {
    if (files.length === 0) { toast.error("Choose a file first."); return; }
    setAnalyzing(true);
    const fd = new FormData(); fd.append("file", files[0]);
    try {
      const { data } = await api.post("/documents/analyze", fd);
      const d = data.detected || data.suggestion || {};
      setForm((f) => ({ ...f, title: d.title || f.title, category: d.category || f.category, number: d.number || f.number, issue_date: d.issue_date || f.issue_date, expiry_date: d.expiry_date || f.expiry_date }));
      setAiFilled({ title: !!d.title, category: !!d.category, number: !!d.number, issue_date: !!d.issue_date, expiry_date: !!d.expiry_date, needs_manual: !!data.needs_manual });
      toast.success(data.needs_manual ? "Couldn't fully read it — please confirm the details" : "AI filled the details — please confirm");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const share = async (it) => {
    const daysStr = window.prompt("Link valid for how many days? (1, 7 or 30)", "7");
    if (daysStr === null) return;
    const days = Math.max(1, Math.min(30, parseInt(daysStr, 10) || 7));
    const passcode = (window.prompt("Optional passcode for this link (leave blank for none):", "") ?? "").trim();
    try {
      const { data } = await api.post(`/documents/${it.record_id}/share`, { hours: days * 24, passcode: passcode || undefined });
      await navigator.clipboard.writeText(`${window.location.origin}/shared/doc/${data.token}`);
      toast.success(data.protected ? `Link (${days}d) copied — passcode: ${passcode}` : `Secure link copied — valid ${days} day${days > 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error("Could not create a share link"); }
  };

  const revoke = async (it) => {
    try { await api.post(`/documents/${it.record_id}/revoke`); toast.success("Share link revoked"); load(); }
    catch (e) { toast.error("Could not revoke the link"); }
  };

  const emailShare = async (it) => {
    try {
      const { data } = await api.post(`/documents/${it.record_id}/share`, { hours: 7 * 24 });
      const link = `${window.location.origin}/shared/doc/${data.token}`;
      const subj = (user?.mail_subject ? user.mail_subject + " " : "") + `Document: ${it.title}`;
      const sig = user?.mail_signature ? `\n\n${user.mail_signature}` : "";
      const subject = encodeURIComponent(subj);
      const body = encodeURIComponent(`Hi,\n\nHere is my document "${it.title}"${it.data?.category ? ` (${it.data.category})` : ""}:\n${link}\n\n(Secure link, valid for 7 days.)${sig}`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
      toast.success("Opening your email app — the message is sent from your own address, not Suffle.");
      load();
    } catch (e) { toast.error("Could not prepare the email share"); }
  };

  const submit = async () => {
    if (files.length === 0) { toast.error("Choose one or more files."); return; }
    setSaving(true);
    let okc = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const single = files.length === 1;
      const meta = single ? form : (bulkMeta[i] || { title: f.name.replace(/\.[^.]+$/, ""), category: "Other" });
      const fd = new FormData();
      fd.append("file", f);
      fd.append("title", meta.title || f.name.replace(/\.[^.]+$/, ""));
      fd.append("category", meta.category || "Other");
      fd.append("number", meta.number || "");
      fd.append("issue_date", meta.issue_date || "");
      fd.append("expiry_date", meta.expiry_date || "");
      fd.append("family_member", meta.family_member || "");
      try { await api.post("/documents/upload", fd); okc++; }
      catch (e) { toast.error(`${f.name}: ${formatApiError(e.response?.data?.detail)}`); }
    }
    if (okc) {
      toast.success(`${okc} document${okc > 1 ? "s" : ""} uploaded securely`);
      setOpen(false); setForm({ title: "", category: "Passport", number: "", issue_date: "", expiry_date: "", family_member: "" }); setFiles([]); setBulkMeta([]);
      load();
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try { await api.delete(`/records/${id}`); setItems((x) => x.filter((y) => y.record_id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div>
      <PageHeader title="My Documents" icon={FileText}
        subtitle="A secure wallet for tickets, passports, visas, invoices and more. Files are validated and scanned on upload."
        actions={<button onClick={() => setOpen(true)} data-testid="upload-doc-btn" className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white"><Upload className="h-4 w-4" /> Upload Document</button>} />

      <div className="mb-5 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /> Uploads are validated (type, size, signature) and blocked if they fail security checks. Files are private to your account and served over short-lived authorized links. We'll remind you before anything expires.
      </div>

      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3" data-testid="email-defaults-bar">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Mail className="h-3.5 w-3.5" /> Email defaults — used when you tap “Email” (sent from your own mail app)</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input value={mailSubj} onChange={(e) => setMailSubj(e.target.value)} placeholder="Subject prefix e.g. [My Trips]" className="h-9" data-testid="mail-subject-input" />
          <div className="flex gap-2">
            <Input value={mailSig} onChange={(e) => setMailSig(e.target.value)} placeholder="Signature e.g. — Roopal, My Store" className="h-9" data-testid="mail-sig-input" />
            <button onClick={saveMailPrefs} data-testid="save-mail-prefs" className="shrink-0 rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white">Save</button>
          </div>
        </div>
      </div>

      {isAgent && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="agent-share-email-bar">
          <span className="text-xs font-medium text-slate-600">Email shown on documents you share:</span>
          <Input value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} placeholder="you@yourstore.com" className="h-9 w-56" data-testid="agent-share-email" />
          <button onClick={saveShareEmail} data-testid="save-share-email" className="rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white">Save</button>
        </div>
      )}

      {items && items.length > 0 && Array.from(new Set(items.map((d) => d.data?.family_member).filter(Boolean))).length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="family-filter">
          <button onClick={() => setFamFilter("")} className={`rounded-full px-3 py-1 text-xs font-medium ${!famFilter ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-500"}`}>All</button>
          {Array.from(new Set(items.map((d) => d.data?.family_member).filter(Boolean))).map((m) => (
            <button key={m} onClick={() => setFamFilter(m)} data-testid={`fam-chip-${m}`} className={`rounded-full px-3 py-1 text-xs font-medium ${famFilter === m ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-500"}`}>{m}</button>
          ))}
        </div>
      )}

      {items === null ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-32 animate-pulse rounded-2xl bg-white/70" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={FileText} title="Your document wallet is empty" description="Upload a passport, visa, ticket or invoice to keep it safe and get expiry reminders."
          action={<button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white"><Upload className="h-4 w-4" /> Upload Document</button>} testid="empty-documents" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="documents-list">
          {items.filter((it) => !famFilter || it.data?.family_member === famFilter).map((it, i) => (
            <motion.div key={it.record_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="card-soft card-hover group p-4" data-testid="document-item">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50 text-purple-500"><FileText className="h-5 w-5" /></span>
                  <div><h3 className="font-semibold text-slate-800">{it.title}</h3><p className="text-xs text-slate-400">{it.data?.category}</p></div>
                </div>
                <button onClick={() => remove(it.record_id)} data-testid="delete-doc" className="rounded-lg p-1.5 text-slate-300 opacity-0 transition hover:bg-rose-50 hover:text-rose-500 group-hover:opacity-100"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusPill status={it.status} />
                {it.data?.expiry_date && <span className="text-xs text-slate-500">Expires {fmtDate(it.data.expiry_date)}</span>}
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-600"><ShieldCheck className="h-3 w-3" /> Scan passed</span>
              </div>
              {it.data?.has_file && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={`${API}/documents/${it.record_id}/file`} target="_blank" rel="noreferrer noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300" data-testid="open-doc-file">
                    <ExternalLink className="h-3 w-3" /> Open
                  </a>
                  <a href={`${API}/documents/${it.record_id}/file?as_=pdf`} target="_blank" rel="noreferrer noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-rose-300 hover:text-rose-500" data-testid="download-doc-pdf">
                    <Download className="h-3 w-3" /> PDF
                  </a>
                  <button onClick={() => share(it)} data-testid="share-doc" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600">
                    <Share2 className="h-3 w-3" /> Copy link
                  </button>
                  <button onClick={() => emailShare(it)} data-testid="email-doc" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-indigo-300 hover:text-indigo-600">
                    <Mail className="h-3 w-3" /> Email
                  </button>
                  {it.data?.share_token && (
                    <button onClick={() => revoke(it)} data-testid="revoke-doc" className="inline-flex items-center gap-1 rounded-lg border border-amber-200 px-3 py-1.5 text-xs font-medium text-amber-600 hover:bg-amber-50">
                      <Ban className="h-3 w-3" /> Revoke
                    </button>
                  )}
                </div>
              )}
              {it.data?.share_token && (
                <p className="mt-2 text-[10px] text-slate-400" data-testid="share-analytics">
                  Link active{it.data.share_pass ? " · passcode set" : ""} · opened {it.data?.share_opens?.length || 0}×
                  {it.data?.share_opens?.length ? ` · last ${new Date(it.data.share_opens[it.data.share_opens.length - 1].at).toLocaleDateString("en-GB")}` : ""}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setAiFilled(null); }}>
        <DialogContent data-testid="upload-doc-dialog">
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {files.length <= 1 && (<>
            {aiFilled && (
              <div className={`flex items-start gap-2 rounded-xl border p-2.5 text-xs ${aiFilled.needs_manual ? "border-amber-300 bg-amber-50 text-amber-700" : "border-indigo-200 bg-indigo-50 text-indigo-700"}`} data-testid="ai-fill-banner">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {aiFilled.needs_manual ? "AI couldn't fully read this file — please fill in / confirm the details below." : "AI read these from your file — please review and edit anything that's wrong before saving."}
              </div>
            )}
            <div><Label className="text-xs text-slate-500">Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="My Passport" className={`mt-1 ${aiFilled?.title ? "ring-2 ring-indigo-300" : ""}`} data-testid="doc-title" /></div>
            <div><Label className="text-xs text-slate-500">Type</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1" data-testid="doc-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs text-slate-500">Reference No.</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className={`mt-1 ${aiFilled?.number ? "ring-2 ring-indigo-300" : ""}`} data-testid="doc-number" /></div>
              <div><Label className="text-xs text-slate-500">Issue Date</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className={`mt-1 ${aiFilled?.issue_date ? "ring-2 ring-indigo-300" : ""}`} data-testid="doc-issue" /></div>
              <div><Label className="text-xs text-slate-500">Expiry Date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className={`mt-1 ${aiFilled?.expiry_date ? "ring-2 ring-indigo-300" : ""}`} data-testid="doc-expiry" /></div>
            </div>
            <div><Label className="text-xs text-slate-500">Belongs to (family member, optional)</Label><Input value={form.family_member} onChange={(e) => setForm({ ...form, family_member: e.target.value })} placeholder="e.g. Mom, Aarav" className="mt-1" data-testid="doc-family" /></div>
            {files.length >= 1 && (
              <button onClick={analyze} disabled={analyzing} data-testid="doc-analyze-btn" className="flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-100 disabled:opacity-50">
                {analyzing ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading document…</> : <><Sparkles className="h-4 w-4" /> Re-run AI auto-detect</>}
              </button>
            )}
            </>)}
            {files.length > 1 && (
              <div className="space-y-2" data-testid="bulk-list">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">{files.length} files — confirm each</span>
                  <button onClick={analyzeBulk} disabled={analyzing} data-testid="bulk-analyze-btn" className="inline-flex items-center gap-1 rounded-full bg-blue-500 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
                    {analyzing ? <><Loader2 className="h-3 w-3 animate-spin" /> Classifying…</> : <><Sparkles className="h-3 w-3" /> AI-classify all</>}
                  </button>
                </div>
                <div className="max-h-56 space-y-2 overflow-auto pr-1">
                  {files.map((f, i) => (
                    <div key={i} className="rounded-lg border border-slate-100 p-2" data-testid="bulk-row">
                      <p className="mb-1 truncate text-[11px] text-slate-400">{f.name}</p>
                      <div className="flex gap-2">
                        <Input value={bulkMeta[i]?.title || ""} onChange={(e) => setBulkMeta((b) => b.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder="Title" className="h-8 text-sm" data-testid={`bulk-title-${i}`} />
                        <Select value={bulkMeta[i]?.category || "Other"} onValueChange={(v) => setBulkMeta((b) => b.map((x, j) => j === i ? { ...x, category: v } : x))}>
                          <SelectTrigger className="h-8 w-32 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div><Label className="text-xs text-slate-500">Files — any common format (PDF, images, Office docs · max 15MB each)</Label>
              <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files || [])); }}
                className="mt-1 rounded-xl border-2 border-dashed border-slate-200 p-5 text-center transition hover:border-blue-300">
                <input id="docfiles" type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="doc-file-input" className="hidden" />
                <label htmlFor="docfiles" className="cursor-pointer text-sm font-medium text-blue-600">Click to choose or drag &amp; drop files here</label>
                {files.length > 0 && <p className="mt-2 text-xs text-slate-500">{files.length} file(s) — auto-sorted by detected type: {files.map((f) => f.name).join(", ")}</p>}
              </div></div>
            <div className="flex items-start gap-1.5 rounded-lg bg-slate-50 p-2.5 text-[11px] text-slate-500"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Files are validated and scanned. Anything suspicious is blocked and never stored.</div>
          </div>
          <button onClick={submit} disabled={saving} data-testid="doc-save" className="mt-2 w-full rounded-xl brand-gradient-bg py-2.5 font-semibold text-white disabled:opacity-50">{saving ? "Uploading..." : "Upload securely"}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
